<template>
    <aside
        :aria-hidden="!open"
        class="stage-filter-sidebar stage-filter-panel absolute inset-y-0 right-0 w-80 overflow-auto z-40"
        :class="open ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
    >
        <div class="stage-filter-body">
            <h2 class="stage-filter-title">{{ $t('Sorting') }}</h2>

            <div class="stage-filter-label">{{ $t('Search') }}</div>
            <div class="mb-5">
                <div class="relative flex-grow">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                    </div>
                    <input
                        v-model.lazy="searchFilterProxy"
                        class="stage-filter-input form-input block w-full pl-10 text-sm"
                        :placeholder="$t('Search')"
                        type="text"
                        @change="$emit('filter-change')"
                    >
                </div>
            </div>

            <div class="stage-filter-label">{{ $t('Period') }}</div>
            <div class="mb-5">
                <select
                    v-model="filterSelectionProxy"
                    class="stage-filter-input block form-select text-sm leading-5 font-medium"
                    @change="$emit('apply-filter-selection')"
                >
                    <option value="current">{{ $t('Current') }}</option>
                    <option
                        v-for="year in availableYears"
                        :key="'timeline-year-' + year"
                        :value="'year:' + year"
                    >
                        {{ $t('Year view') }} {{ year }}
                    </option>
                </select>
            </div>

            <div class="stage-filter-label">{{ $t('Entries') }}</div>
            <div class="mb-5">
                <select
                    v-model="positionFilterSelectionProxy"
                    class="stage-filter-input block form-select text-sm leading-5 font-medium"
                    @change="$emit('filter-change')"
                >
                    <option value="all">{{ $t('All entries') }}</option>
                    <option value="with_dates">{{ $t('All entries with schedule data') }}</option>
                    <option value="custom_entries">{{ $t('Custom entries') }}</option>
                </select>
            </div>

            <div v-if="canFilterCompanies" class="stage-filter-label">{{ $t('Companies') }}</div>
            <div v-if="canFilterCompanies" class="mb-5">
                <input-select
                    id="timeline-company-filter"
                    v-model="companyFilterSelectionProxy"
                    class="stage-filter-select"
                    :options="companyFilterDropdownOptions"
                    option-label="name"
                    @change="$emit('filter-change')"
                >
                    <template v-slot:selectedOption="props">
                        <template v-if="!props || !props.anySelected">
                            <span class="block truncate">{{ $t('All companies') }}</span>
                        </template>
                        <template v-else>
                            <span class="block truncate">{{ props.option ? props.option.name : $t('All companies') }}</span>
                        </template>
                    </template>
                    <template v-slot:selectOption="props">
                        <div class="flex items-center space-x-3">
                            <div v-if="props.option && props.option.id !== 'all' && props.option.avatarUrl" class="flex-shrink-0 inline-block">
                                <img :src="props.option.avatarUrl" alt="Avatar" class="w-5 h-5 rounded-sm object-cover">
                            </div>
                            <div
                                class="block truncate"
                                :class="String(props.option.id) === String(companyFilterSelection) ? 'font-semibold' : 'font-normal'"
                            >
                                {{ props.option.name }}
                            </div>
                        </div>
                    </template>
                </input-select>
            </div>

            <div class="stage-filter-label">{{ $t('Sort by') }}</div>
            <div class="mb-5">
                <select
                    v-model="sortSelectionProxy"
                    class="stage-filter-input block form-select text-sm leading-5 font-medium"
                    @change="$emit('filter-change')"
                >
                    <option value="title_asc">{{ $t('Title ascending') }}</option>
                    <option value="title_desc">{{ $t('Title descending') }}</option>
                    <option value="updated_at_desc">{{ $t('Update descending') }}</option>
                    <option value="updated_at_asc">{{ $t('Update ascending') }}</option>
                    <option value="created_at_desc">{{ $t('Created descending') }}</option>
                    <option value="created_at_asc">{{ $t('Created ascending') }}</option>
                </select>
            </div>

            <div class="stage-filter-label">{{ $t('Items per page') }}</div>
            <div>
                <select
                    v-model.number="perPageSelectionProxy"
                    class="stage-filter-input block form-select text-sm leading-5 font-medium"
                    @change="$emit('filter-change')"
                >
                    <option :value="10">10</option>
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                </select>
            </div>
            <div v-if="hasActiveFilters" class="mt-4">
                <button
                    id="positions-timeline-filter-reset-btn"
                    type="button"
                    class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                    @click="$emit('reset')"
                >
                    {{ $t('Reset selection') }}
                </button>
            </div>
        </div>
    </aside>
</template>

<script>
export default {
    name: 'timeline-filter-sidebar',
    props: {
        open: {type: Boolean, default: false},
        searchFilter: {type: String, default: ''},
        filterSelection: {type: String, default: 'current'},
        availableYears: {type: Array, default: () => []},
        positionFilterSelection: {type: String, default: 'with_dates'},
        canFilterCompanies: {type: Boolean, default: false},
        companyFilterSelection: {type: [String, Number], default: 'all'},
        companyFilterDropdownOptions: {type: Array, default: () => []},
        sortSelection: {type: String, default: 'title_asc'},
        perPageSelection: {type: Number, default: 50},
        hasActiveFilters: {type: Boolean, default: false},
    },
    emits: [
        'update:searchFilter',
        'update:filterSelection',
        'update:positionFilterSelection',
        'update:companyFilterSelection',
        'update:sortSelection',
        'update:perPageSelection',
        'filter-change',
        'apply-filter-selection',
        'reset',
    ],
    computed: {
        searchFilterProxy: {
            get() {
                return this.searchFilter;
            },
            set(value) {
                this.$emit('update:searchFilter', value);
            },
        },
        filterSelectionProxy: {
            get() {
                return this.filterSelection;
            },
            set(value) {
                this.$emit('update:filterSelection', value);
            },
        },
        positionFilterSelectionProxy: {
            get() {
                return this.positionFilterSelection;
            },
            set(value) {
                this.$emit('update:positionFilterSelection', value);
            },
        },
        companyFilterSelectionProxy: {
            get() {
                return this.companyFilterSelection;
            },
            set(value) {
                this.$emit('update:companyFilterSelection', value);
            },
        },
        sortSelectionProxy: {
            get() {
                return this.sortSelection;
            },
            set(value) {
                this.$emit('update:sortSelection', value);
            },
        },
        perPageSelectionProxy: {
            get() {
                return this.perPageSelection;
            },
            set(value) {
                this.$emit('update:perPageSelection', value);
            },
        },
    },
};
</script>
